import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import API from '../../../services/API'
import AssignShiftComponent from './AssignShiftComponent'
import { PTSDutiesContext } from '.'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import EventIcon from '@mui/icons-material/Event'
import {
  Alert,
  IconButton,
  LinearProgress,
  Tooltip,
} from '@mui/material'
import StaffSelector from '../../events/StaffSelector'
import { Campaign } from '@mui/icons-material'
import SmallModal from '../../../components/Modals/SmallModal'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import EmployeeSelector from '../../events/EmployeeSelector'

const UncoveredShifts = () => {
  const {
    loading,
    duties,
    getUnassignedShifts,
    getUncoveredShifts,
    setCreateModalOpen,
    reloadDuty,
    uncoveredShifts,
    ptsDutyID,
  } = useContext(PTSDutiesContext)
  const [assignShiftOpen, setAssignShiftOpen] = useState(null)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [assigningShift, setAssigningShift] = useState(false)
  const [assignmentError, setAssignmentError] = useState('')

  const [openAvailableShiftEmail, setOpenAvailableShiftEmail] = useState(false)
  const [loadingAdhocEmail, setLoadingAdhocEmail] = useState(false)

  const { setToast } = useContext(GlobalContext)

  const getSelectedEmployeeId = () =>
    selectedEmployee?.employee?._id ??
    selectedEmployee?._id ??
    selectedEmployee ??
    null

  const getAssignmentErrorMessage = (res) => {
    if (res?.data?.issues?.length) {
      return res.data.issues.map((issue) => issue.message).join(' ')
    }

    return res?.data?.error || 'Unable to assign this shift.'
  }

  const handleAssignToEmployee = async () => {
    const employeeId = getSelectedEmployeeId()

    if (!employeeId) {
      setAssignmentError('Please select an employee before assigning the shift.')
      return
    }

    setAssigningShift(true)
    setAssignmentError('')

    try {
      const res = await API.put(`/pts/shift/${assignShiftOpen._id}`, {
        assignedTo: employeeId,
      })

      if (res.status === 200) {
        reloadDuty()
        getUncoveredShifts()
        getUnassignedShifts()
        setAssignShiftOpen(null)
        setSelectedEmployee(null)
        return
      }

      setAssignmentError(getAssignmentErrorMessage(res))
    } catch (e) {
      console.error(e)
      setAssignmentError('Unable to assign this shift. Please try again.')
    } finally {
      setAssigningShift(false)
    }
  }

  // Organisation-level switch (Configuration > Shifts). Missing = enabled.
  const manualShiftNotificationsEnabled =
    useContext(TenantSettingsContext).orgConfig?.orgSettings
      ?.shiftAvailabilityNotifications?.manual !== false

  const sendAvailableShiftEmailToAllStaff = () => {
    setLoadingAdhocEmail(true)
    API.post('/shifts/availableshiftsad-hoc', {
      uncoveredShifts: uncoveredShifts.map((s) => s._id),
    })
      .then((res) => {
        setLoadingAdhocEmail(false)
        setOpenAvailableShiftEmail(false)
        if (res.status === 200) {
          const count = res.data?.notified
          setToast({
            message:
              typeof count === 'number'
                ? `Notification sent to ${count} staff member${count === 1 ? '' : 's'}`
                : 'Notification sent',
          })
        } else {
          setToast({
            severity: 'error',
            message:
              res.data?.message ||
              'Unable to send the notification. Please try again later.',
          })
        }
      })
      .catch((e) => {
        setLoadingAdhocEmail(false)
        setOpenAvailableShiftEmail(false)
        setToast({
          severity: 'error',
          message: 'Unable to send email. Please try again later.',
        })
        console.error(e)
      })
  }

  return (
    <Box
      bgcolor="#fff"
      p={2}
      borderRadius={2}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1}
        mb={2}
        width="100%"
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <EventIcon color="anBlue" />
          <Typography variant="subtitle1" fontWeight={600} color="anBlue.main">
            Uncovered Shifts
          </Typography>
        </Stack>
        {manualShiftNotificationsEnabled && (
          <IconButton
            size="small"
            color="primary"
            onClick={() => setOpenAvailableShiftEmail(true)}
          >
            <Tooltip title="Notify eligible staff of available shifts">
              <Campaign fontSize="small" />
            </Tooltip>
          </IconButton>
        )}
      </Stack>
      <SmallModal
        open={openAvailableShiftEmail}
        onClose={() => setOpenAvailableShiftEmail(false)}
      >
        <Stack gap={1} alignItems="center">
          <Typography variant="body2" textAlign="center">
            Notify all staff whose roles match these uncovered shifts, by
            email and/or app push according to their own notification
            preferences.
          </Typography>
          <Typography variant="subtitle2" textAlign="center">
            The automatic daily and weekly digests are unaffected and can be
            switched off in Configuration &gt; Shifts.
          </Typography>
          <Button
            onClick={() => sendAvailableShiftEmailToAllStaff()}
            color="anGreen"
            variant="contained"
            fullWidth
            disabled={loadingAdhocEmail}
          >
            {loadingAdhocEmail ? 'Sending' : 'Send Now'}
          </Button>
          <Button
            onClick={() => setOpenAvailableShiftEmail(false)}
            color="anRed"
            variant="outlined"
            fullWidth
          >
            Cancel
          </Button>
        </Stack>
      </SmallModal>
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
        {loading ? (
          <Box textAlign="center" mt={4}>
            <LinearProgress size={24} />
          </Box>
        ) : uncoveredShifts.length ? (
          uncoveredShifts.map((us) => (
            <AssignShiftComponent
              shift={us}
              key={us._id}
              buttonText="Assign To Employee"
              onClick={() => setAssignShiftOpen(us)}
            />
          ))
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            mt={2}
          >
            All Shifts Covered
          </Typography>
        )}
      </Box>
      <Dialog
        fullWidth
        open={!!assignShiftOpen}
        onClose={() => {
          setAssignShiftOpen(null)
          setSelectedEmployee(null)
          setAssignmentError('')
        }}
      >
        <Stack padding={1.5} gap={1} alignItems="center">
          <Typography variant="title2" fontSize="1.2rem">
            Assign Employee to Shift
          </Typography>
          <Typography variant="caption">
            {moment(assignShiftOpen?.start).format('Do MMM HH:mm')} -{' '}
            {moment(assignShiftOpen?.end).format('HH:mm')}
          </Typography>
          <EmployeeSelector value={selectedEmployee} onChange={(e, newValue) => {
            setSelectedEmployee(newValue)
            setAssignmentError('')
          }} fullWidth />
          {assignmentError && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {assignmentError}
            </Alert>
          )}
          <Button
            fullWidth
            color="anBlue"
            variant="contained"
            onClick={() => handleAssignToEmployee()}
            disabled={assigningShift || !selectedEmployee}
          >
            {assigningShift ? 'Assigning...' : 'Submit'}
          </Button>
        </Stack>
      </Dialog>
    </Box>
  )
}

export default UncoveredShifts
